import { Idea, MediaIdea } from "../types/Idea.interface";

export const CARD_LAYOUT_MAP = {
  desktop: { count: 16, mediaCount: 2 },
  tablet: { count: 10, mediaCount: 2 },
  mobile: { count: 5, mediaCount: 1 },
};

type DeviceType = "desktop" | "tablet" | "mobile";

interface SplitResult {
  top: {
    normal: Idea[];
    media: Idea[];
  };
  bottom: {
    normal: Idea[];
    media: Idea[];
  };
  remaining: {
    normal: Idea[];
    media: Idea[];
  };
}

export function splitCardsIntoBlocks(
  normalCards: Idea[],
  mediaCards: Idea[],
  deviceType: DeviceType
): SplitResult {
  const normal = [...normalCards];
  const media = [...mediaCards];

  const take = <T>(arr: T[], count: number): T[] => arr.splice(0, count);

  const fillBlock = (
    normals: Idea[],
    medias: Idea[]
  ): { normal: Idea[]; media: Idea[] } => {
    let normal: Idea[] = [];
    let media: Idea[] = [];

    if (deviceType === "mobile") {
      const normalTarget = 5;
      const mediaTarget = 1;

      const normalsTaken = take(normals, normalTarget);
      const mediasTaken = take(medias, mediaTarget);

      const total = normalsTaken.length + mediasTaken.length;

      if (total < 6) {
        const spaceLeft = 6 - total;

        if (normalsTaken.length < normalTarget) {
          normalsTaken.push(...take(normals, spaceLeft));
        }

        const newTotal = normalsTaken.length + mediasTaken.length;
        if (newTotal < 6) {
          mediasTaken.push(...take(medias, 6 - newTotal));
        }
      }

      return {
        normal: normalsTaken,
        media: mediasTaken,
      };
    }

    if (deviceType === "desktop") {
      if (normals.length > 0 && normals.length <= 4 && medias.length >= 2) {
        normal = take(normals, normals.length);
        media = take(medias, 2);
      } else if (normals.length > 0 && medias.length > 0) {
        normal = take(normals, Math.min(8, normals.length));
        media = take(medias, 1);
      } else if (normals.length === 0 && medias.length >= 4) {
        media = take(medias, 4);
      } else if (normals.length === 0 && medias.length > 0) {
        media = take(medias, medias.length);
      } else if (normals.length > 0) {
        normal = take(normals, Math.min(8, normals.length));
      }

      return { normal, media };
    }

    if (deviceType === "tablet") {
      if (normals.length >= 5 && medias.length >= 1) {
        normal = take(normals, 5);
        media = take(medias, 1);
      } else if (normals.length >= 3 && medias.length >= 2) {
        normal = take(normals, 3);
        media = take(medias, 2);
      } else if (normals.length === 0 && medias.length >= 4) {
        media = take(medias, 4);
      } else if (normals.length === 0 && medias.length > 0) {
        media = take(medias, medias.length);
      } else if (normals.length >= 6) {
        normal = take(normals, 6);
      } else if (normals.length > 0) {
        normal = take(normals, normals.length);
      }

      return { normal, media };
    }

    return { normal: [], media: [] };
  };

  const top = fillBlock(normal, media);
  const bottom =
    deviceType === "mobile"
      ? { normal: [], media: [] }
      : fillBlock(normal, media);

  const result = {
    top,
    bottom,
    remaining: { normal, media },
  };

  return result;
}

export const mapMediaCardToIdea = (mediaCard: MediaIdea) => {
  const { ideaItem } = mediaCard;

  return {
    id: ideaItem.id,
    ideaName: ideaItem.title,
    author: mediaCard.user,
    tag: { name: ideaItem.categoryName },
    watermarkAttachmentId: mediaCard.photoAttachment?.id,
    videoAttachment: mediaCard.videoAttachment?.id,
    liked: mediaCard.likedByMe ?? false,
    rating: mediaCard.likes ?? 0,
    isMediaCard: true,
    mediaIdeaId: mediaCard.id ?? "",
  };
};

export const getLayoutConfig = (
  type: "top" | "bottom",
  isDesktop: boolean,
  isTablet: boolean
) => {
  if (isDesktop) {
    return type === "top"
      ? { mediaIndex: 6, squareIndexes: [4, 5, 7, 8], normalRequired: 8 }
      : { mediaIndex: 4, squareIndexes: [3, 5, 6, 7], normalRequired: 8 };
  }

  if (isTablet) {
    return type === "top"
      ? { mediaIndex: 4, squareIndexes: [2, 3, 5, 6], normalRequired: 5 }
      : { mediaIndex: 3, squareIndexes: [2, 4, 5, 6], normalRequired: 5 };
  }

  // Для мобилок вставим медиа в позицию)
  return {
    mediaIndex: 4,
  };
};
